import ced from '@/services/api/ced/ced';
import JelWorkspace from '@/store/classes/jelWorkspace';

import { inMgnl } from '@/helpers/AppHelpers';
import { PageMappingCasino } from '@/helpers/PageMappingHelpers';
import i18n from '@/js/i18n';

const jelWorkspace = new JelWorkspace({
  module: 'cedTable',
  refreshMinProd: process.env.VUE_APP_STORE_CED_TABLE_REFRESH_MIN,
  refreshMinDev: process.env.VUE_APP_STORE_DEV_REFRESH_MIN,
});

const getDefaultState = () => {
  const refreshMillisecondeLive =
    (process.env.NODE_ENV == 'production'
      ? process.env.VUE_APP_STORE_CED_TABLE_LIVE_REFRESH_MIN
      : Math.min(
          process.env.VUE_APP_STORE_CED_TABLE_LIVE_REFRESH_MIN,
          process.env.VUE_APP_STORE_DEV_REFRESH_MIN
        )) *
    60 *
    1000;

  return {
    ...jelWorkspace.state,
    categorie: 'casinoendirect',
    refreshMillisecondeLive,
    listeLive: null,
  };
};

const state = getDefaultState();

const getters = {
  ...jelWorkspace.getters,
  getLienInterneFromProduit:
    (state, getters) =>
    (produit, urlParams = null) => {
      return {
        field: 'lienInterne',
        lienInterne: getProduitRouteFromProduit(state, getters, produit),
        urlParams,
        isDejaNettoye: true,
      };
    },

  getFamilleFromProduit: (state, getters) => (produit) => {
    return produit['@path'].split('/')[2];
  },

  // Retourne union de liste et listeLive
  // Tient compte de sources multiples (Canada, Europe, etc...)
  getTableInfoFromUuid: (state, getters) => (uuidTable, isMobile) => {
    if (!state.liste) {
      return null;
    }
    const table = getters.getByUuid(uuidTable);
    return getTableInfo(state, table, isMobile);
  },

  getTableInfoFromName: (state, getters) => (name, isMobile) => {
    if (!state.liste) {
      return null;
    }
    const table = getters.getByName(name);
    return getTableInfo(state, table, isMobile);
  },

  // Retourne un array trié/filtré de Produits par idProduitCollection
  // Retourne un array trié/filtré de Produits par idProduitCollection
  getArrayProduitByProduitCollection:
    (state, getters, rootState, rootGetters) => (produitCollection) => {
      // Ne rien retourner si la liste n'est pas encore initilisée
      if (!state.liste) {
        return [];
      }

      // TODO: Pas implémenté pour CED
      // Si on a une liste fixe, on la retourne
      if (produitCollection.listeProduit && produitCollection.listeProduit.length > 0) {
        const listeProduit = state.liste.filter((produit) => {
          return produitCollection.listeProduit.find((produitId) => {
            return produit['@id'] == produitId;
          });
        });
        return listeProduit;
      }

      // FILTRER
      const listeProduit = state.liste.filter((produit) => {
        // On cherche à éliminer des produits en retournant false

        const tableInfo = getters.getTableInfoFromUuid(produit['@id']);
        // console.log(tableInfo);

        // Famille
        // TODO:
        // if (produitCollection.idFamille && ...)
        //   return false;

        // Statut (ouvert,ouvertBientot,ferme)
        if (
          produitCollection.indTableOuvert == 'true' &&
          tableInfo &&
          tableInfo.statut !== 'ouvert'
        )
          return false;
        if (
          produitCollection.indTableOuvertBientot == 'true' &&
          tableInfo &&
          tableInfo.statut !== 'ouvertBientot'
        )
          return false;
        if (produitCollection.indTableFerme == 'true' && tableInfo && tableInfo.statut !== 'ferme')
          return false;

        // Mise min (!!: si n'est pas null ou zéro)
        if (!!produitCollection.miseIntervalleMin || !!produitCollection.miseIntervalleMax) {
          if (
            parseFloat(tableInfo.betLimits?.CAD?.min) <
              parseFloat(produitCollection.miseIntervalleMin) ||
            parseFloat(tableInfo.betLimits?.CAD?.min) >
              parseFloat(produitCollection.miseIntervalleMax)
          ) {
            return false;
          }
        }

        // Présentateur
        // TODO:
        // if (produitCollection.presentateur && produit.presentateur != produitCollection.presentateur) {
        //   return false;
        // }

        // Est affiché
        return true;
      });

      // TRIER
      // TODO:
      // listeProduit.sort((produit1, produit2) => {
      //   // TODO: date désactivation (en preuve de concept, on ne vérifie que la présence de dateDesactivationDebut)
      //   // Jeu actifs en premier / inactifs en dernier
      //   if (!produit1.dateDesactivationDebut && produit2.dateDesactivationDebut) return -1;
      //   if (produit1.dateDesactivationDebut && !produit2.dateDesactivationDebut) return 1;

      //   // Date de lancement du plus récent au plus ancien
      //   if (produit1.dateLancement > produit2.dateLancement) return -1;
      //   if (produit1.dateLancement < produit2.dateLancement) return 1;
      // });

      return listeProduit;
    },

  // Retourne un array trié/filtré de idCedTable par idProduitCollection
  getArrayIdCedTableByProduitCollection:
    (state, getters, rootState, rootGetters) => (produitCollection) => {
      const listeProduit = getters.getArrayProduitByProduitCollection(produitCollection);

      // Retourner la liste de uuid des produits
      const arrIdProduit = listeProduit.map((produit) => produit['@id']);

      return arrIdProduit;
    },
};

const actions = {
  ...jelWorkspace.actions,
  refreshLive(context) {
    if (!context.rootState.idleVue.isIdle) {
      ced
        .getCedListeLive()
        .then((listeLive) => {
          context.commit('SET_LISTE_LIVE', listeLive);
        })
        .catch((error) => console.log(error));
    }

    // Délai de raffaichissement: 60 sec en PO, 30 sec en DEV.
    setTimeout(() => {
      context.dispatch('refreshLive');
    }, context.state.refreshMillisecondeLive);
  },
};

const mutations = {
  ...jelWorkspace.mutations,
  async INIT(state) {
    if (!state.isLoaded) {
      this.commit(`${state.module}/INTERNAL_INIT`);
      if (!inMgnl) {
        await ced
          .getCedListeLive()
          .then((listeLive) => {
            this.commit(`${state.module}/SET_LISTE_LIVE`, listeLive);
          })
          .catch((error) => console.log(error));
        if (state.refreshMillisecondeLive) {
          this.dispatch(`${state.module}/refreshLive`);
        }
      }
    }
  },
  SET_LISTE_LIVE(state, listeLive) {
    state.listeLive = listeLive;
  },
};

function getProduitRouteFromProduit(state, getters, produit) {
  const pageMapping =
    i18n.locale == 'en'
      ? PageMappingCasino.CASINO_PRODUIT.siteUrlEn
      : PageMappingCasino.CASINO_PRODUIT.siteUrl;
  const categorie = i18n.t(`casino.categorie.${state.categorie}.slug`);
  const famille = i18n.t(
    `casino.categorie.${state.categorie}.famille.${getters.getFamilleFromProduit(produit)}.slug`
  );
  const name = i18n.locale == 'en' ? produit.nameEn : produit.name;
  return `${pageMapping}/${categorie}/${famille}/${name}`;
}

// Retourne union de liste et listeLive
// Tient compte de sources multiples (Canada, Europe, etc...)
function getTableInfo(state, table, isMobile) {
  // TODO: Retirer ces 2 lignes lorsque tous les name/nameEn seront renseignés (après MEP)
  table.name = table.name || table['@name'];
  table.nameEn = table.nameEn || table['@name'];
  const idTable = isMobile ? table.idTableMobile : table.idTableDesktop;
  // Chercher dans quelle source (Canada, Europe, etc...)
  const tableListeLiveSource = state.listeLive?.find((tableListeLiveSource) => {
    // Si une source est inactive, on a un noeud à null
    return (
      tableListeLiveSource &&
      tableListeLiveSource.tables &&
      tableListeLiveSource.tables[idTable] != undefined
    );
  });

  if (tableListeLiveSource) {
    return {
      ...tableListeLiveSource.tables[idTable],
      // 'table' doit être après 'tableListeLiveSource' car overide 'name'
      ...table,
      source: tableListeLiveSource.source,
      evolutionPublicDomain: tableListeLiveSource.evolutionPublicDomain,
      evolutionChannelDesktop: tableListeLiveSource.evolutionChannelDesktop,
      evolutionChannelMobile: tableListeLiveSource.evolutionChannelMobile,
      videoSnapshotBG: videoSnapshotBG(
        tableListeLiveSource.tables[idTable],
        tableListeLiveSource.evolutionPublicDomain
      ),
      // TODO: Ajouter ouvertBientot, mettre en fonction, constante...
      statut: tableListeLiveSource.tables[idTable] ? 'ouvert' : 'ferme',
    };
  }

  return table;
}

function videoSnapshotBG(aTableLive, domaine) {
  return (
    domaine + aTableLive.videoSnapshot.links[imageSize()]
    // TODO: Encore nécessaire?
    //  +
    // "&refreshCount=" +
    // this.refreshCount
  );
}

function imageSize() {
  // eslint-disable-next-line no-undef
  const winWidth = $(window).width();

  if (winWidth <= 320) return 'S';
  if (winWidth <= 640) return 'M';
  if (winWidth <= 1024) return 'L';
  return 'XL';
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
